import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles } from '@material-ui/core'
import remarkGfm from 'remark-gfm'
import ReactMarkdown from 'react-markdown'
import clsx from 'clsx'
import Icon from '../../../atoms/Icon'
import { useComments } from '../../Comments/CommentContext'
import { useCheckPolicy } from '../../../../hooks'
import { THINGS } from '../../../../policies/admin'
import ThingDialog from '../ThingDialog'

const variants = {
  default: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',

    '& .__thing-header': {
      borderBottom: 'var(--border-neutral-thin)',
      padding: '10px 20px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center'
    },

    '& .__thing-name': {
      fontSize: theme.typography.fontSizes.h5
    },

    '& .__thing-content': {
      padding: '10px 20px'
    },

    '& .__thing-actions': {
      display: 'flex',
      flexDirection: 'row'
    }
  }),
  borderless: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    borderRadius: 'var(--border-radius-md)',

    '& .__thing-header': {
      borderBottom: 'var(--border-neutral-normal)',
      padding: '10px 20px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center'
    },

    '& .__thing-name': {
      fontSize: theme.typography.fontSizes.h5
    },

    '& .__thing-content': {
      padding: '10px 20px',
      fontWeight: theme.typography.weights.light
    },

    '& .__thing-actions': {
      display: 'flex',
      flexDirection: 'row'
    }
  })
}

const mapVariant = (theme, variant) => {
  if (variant in variants) {
    return variants[variant](theme)
  }
  return variants.default(theme)
}

const useStyles = makeStyles((theme) => ({
  thing: ({ variant }) => ({
    ...mapVariant(theme, variant)
  })
}))

function Thing ({
  thing,
  className,
  style,
  showHeader,
  variant,
  commentsIcon,
  attachmentsIcon,
  hideComments,
  hideAttachments,
  hideIfEmpty,
  labels,
  isEditable = false
}) {
  const classes = useStyles({ variant })
  const { showCommentThread, showAttachments } = useComments()
  const thingDialogRef = useRef(null)
  const hasEditThingPermission = useCheckPolicy(THINGS.editThings)

  const openAttachments = useCallback(() => {
    showAttachments({
      clientId: thing.levelId,
      levelTypeId: 279,
      levelId: thing.thingId
    })
  }, [showAttachments, thing])

  const openComments = useCallback(() => {
    showCommentThread({
      clientId: thing.levelId,
      levelTypeId: 279,
      levelId: thing.thingId
    })
  }, [showCommentThread, thing])

  const contentType = thing?.data?.contentType ?? 'html'

  const openThings = useCallback(() => {
    thingDialogRef.current.open({
      name: thing.name,
      code: thing.code,
      content: thing.content,
      ordinal: thing.ordinal,
      title: thing.name,
      asset: {
        levelId: thing.levelId,
        levelTypeId: thing.levelTypeId
      },
      metadata: {
        dialogSize: 'large'
      }
    }, true)
  }, [thing])

  if (!thing && hideIfEmpty) {
    return null
  }

  if (!thing && !hideIfEmpty) {
    return (
      <div className={clsx(classes.thing, className)} style={style}>{labels?.empty || 'N/A'}</div>
    )
  }

  return (
    <div className={clsx(classes.thing, className)} style={style}>
      <ThingDialog ref={thingDialogRef} />
      {showHeader ? (
        <div className='__thing-header'>
          <div>
            <div className='__thing-name'>{thing.name}</div>
          </div>
          <div className='__thing-actions'>
            {hideComments ? null : (
              <IconButton onClick={openComments}>
                <Icon name={commentsIcon} customSize={16} />
              </IconButton>
            )}
            {hideAttachments ? null : (
              <IconButton onClick={openAttachments}>
                <Icon name={attachmentsIcon} customSize={16} />
              </IconButton>
            )}
            {(isEditable && hasEditThingPermission) ? (
              <IconButton onClick={openThings}>
                <Icon name='edit' customSize={16} />
              </IconButton>
            ) : null}
          </div>
        </div>
      ) : null}
      <div style={{ position: 'relative' }}>
        {(isEditable && hasEditThingPermission && !showHeader) ? (
          <div style={{ position: 'absolute', top: 0, right: 0 }}>
            <IconButton onClick={openThings}>
              <Icon name='edit' customSize={16} />
            </IconButton>
          </div>
        ) : null}
        {contentType === 'html' ? (
          <div className='__thing-content'>
            <div dangerouslySetInnerHTML={{ __html: thing.content }} />
          </div>
        ) : contentType === 'markdown' ? (
          <div className='__thing-content'>
            <ReactMarkdown children={thing.content} remarkPlugins={[remarkGfm]} />
          </div>
        ) : null}
      </div>

    </div>
  )
}

Thing.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  thing: PropTypes.shape({
    thingId: PropTypes.number,
    levelId: PropTypes.number,
    levelTypeId: PropTypes.number,
    name: PropTypes.string,
    content: PropTypes.string,
    data: PropTypes.object,
    category: PropTypes.string,
    code: PropTypes.string,
    ordinal: PropTypes.number
  }),
  variant: PropTypes.oneOf(['default', 'borderless']),
  showHeader: PropTypes.bool,
  commentsIcon: PropTypes.string,
  attachmentsIcon: PropTypes.string,
  hideComments: PropTypes.bool,
  hideAttachments: PropTypes.bool,
  hideIfEmpty: PropTypes.bool,
  labels: PropTypes.shape({
    empty: PropTypes.string
  }),
  isEditable: PropTypes.bool
}

Thing.defaultProps = {
  showHeader: true,
  variant: 'default',
  commentsIcon: 'chat',
  attachmentsIcon: 'document',
  labels: {
    empty: 'N/A'
  },
  isEditable: false
}

export default Thing
